// @systeme: interface
// Dans Moi : qui est dans le foyer, inviter quelqu'un, les invitations en attente. Et,
// seulement si au moins une tâche est attribuée, la porte vers « qui fait quoi ».
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { Link as LienRouteur, useNavigate } from 'react-router';
import type { FoyerResume, Invitation, Membre } from '@foyer/shared';
import { ZonesScolaires } from '@foyer/shared';
import { api } from '../lib/api';
import { formaterDateComplete, jourDe } from '../lib/dates';
import { fonctionActive } from '../lib/fonctionnalites';
import { useSession, useSessionConnectee } from '../session/SessionContext';
import { useMessages } from './Messages';

const NOMS_ROLES = { titulaire: 'titulaire', membre: 'membre', invite: 'invité·e' } as const;
const NOMS_ZONES = { A: 'Zone A', B: 'Zone B', C: 'Zone C', corse: 'Corse' } as const;

export function SectionFoyer() {
  const session = useSessionConnectee();
  const { mettreAJour } = useSession();
  const navigate = useNavigate();
  const { dire, direErreur } = useMessages();
  const [membres, setMembres] = useState<Membre[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [foyers, setFoyers] = useState<FoyerResume[]>([]);
  const [attribuees, setAttribuees] = useState(0);
  const [email, setEmail] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.moi.membres().then(setMembres).catch(direErreur);
    api.moi
      .foyers()
      .then(setFoyers)
      .catch(() => undefined);
    api.invitations
      .lister()
      .then(setInvitations)
      .catch(() => undefined);
    api.elements
      .repartition()
      .then((r) => setAttribuees(r.attribuees))
      .catch(() => undefined);
  }, [direErreur]);

  async function inviter() {
    if (email.trim() === '') return;
    setEnCours(true);
    try {
      await api.invitations.creer({ email: email.trim(), role: 'membre' });
      setInvitations(await api.invitations.lister());
      setEmail('');
      dire('L’invitation est partie. Le lien est aussi là, si tu préfères l’envoyer toi-même.');
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  async function copier(jeton: string) {
    const lien = `${window.location.origin}/rejoindre/${jeton}`;
    try {
      await navigator.clipboard.writeText(lien);
      dire('Lien copié.');
    } catch {
      dire(lien);
    }
  }

  return (
    <Box>
      <Typography variant="h2" component="h2" gutterBottom>
        Mon foyer
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Zone scolaire, pour comprendre « avant les vacances »
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={session.espace.zoneScolaire ?? ''}
          onChange={(_e, valeur: string | null) => {
            if (valeur === null) return;
            const zone = ZonesScolaires.find((z) => z === valeur) ?? null;
            void api.moi
              .modifier({ zoneScolaire: zone })
              .then((r) => {
                mettreAJour({
                  ...session,
                  espace: { ...session.espace, zoneScolaire: r.espace.zoneScolaire },
                });
                dire(zone ? 'C’est noté.' : 'Zone effacée.');
              })
              .catch(direErreur);
          }}
          aria-label="Zone scolaire"
        >
          {ZonesScolaires.map((z) => (
            <ToggleButton key={z} value={z}>
              {NOMS_ZONES[z]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {foyers.length > 1 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {foyers.map((f) => (
            <Button
              key={f.id}
              size="small"
              variant={f.courant ? 'contained' : 'outlined'}
              disabled={f.courant}
              onClick={() =>
                void api.moi
                  .choisirFoyer(f.id)
                  .then((s) => {
                    mettreAJour(s);
                    navigate('/');
                  })
                  .catch(direErreur)
              }
            >
              {f.nom}
            </Button>
          ))}
        </Stack>
      )}
      {session.espace.accesSupportJusquau && (
        <Typography color="text.secondary" sx={{ mb: 1.5 }}>
          À ta demande, l’équipe peut regarder ce qui ne va pas dans ton foyer jusqu’au{' '}
          {formaterDateComplete(jourDe(session.espace.accesSupportJusquau))}. Ce qui est privé reste
          privé.
        </Typography>
      )}
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        {membres.map((m) => (
          <Typography key={m.id}>
            {m.prenom}{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              · {NOMS_ROLES[m.role]}
            </Typography>
          </Typography>
        ))}
      </Stack>
      {fonctionActive(session, 'invitations') && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            void inviter();
          }}
        >
          <Stack direction="row" spacing={1}>
            <TextField
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Le courriel de ton conjoint, de ta mère…"
              inputProps={{ 'aria-label': 'Courriel à inviter', inputMode: 'email' }}
            />
            <Button
              type="submit"
              variant="outlined"
              disabled={enCours || email.trim() === ''}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Inviter
            </Button>
          </Stack>
        </Box>
      )}
      {invitations.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {invitations.map((i) => (
            <Stack
              key={i.id}
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {i.email} · en attente
              </Typography>
              <Button size="small" onClick={() => void copier(i.jeton)}>
                Copier le lien
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() =>
                  void api.invitations
                    .annuler(i.id)
                    .then(() => api.invitations.lister())
                    .then(setInvitations)
                    .catch(direErreur)
                }
              >
                Annuler
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
      {attribuees > 0 && (
        <Button component={LienRouteur} to="/moi/qui-fait-quoi" sx={{ mt: 1.5, px: 0 }}>
          Qui fait quoi
        </Button>
      )}
    </Box>
  );
}
